import { useEffect, useState } from "react";
import ContentLayout from "..//components/ContentLayout";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/css/bootstrap.css";
import EventCard from "..//components/Dashboard/EventCard";
import { Stack } from "react-bootstrap";
import {
  CalendarTotal,
  DashboardData,
  EventSummaryByMonth,
} from "../class/DashboardData";
import common from "..//utils/common";
import toast, { Toaster } from "react-hot-toast";
import { BackendApi } from "../utils/BackendApi";
import { ApiRoute } from "../utils/ApiRoute";
import { AxiosResponse } from "axios";
import moment from "moment";
import { cal_holiday, cal_record } from "../class/prisma/db";
import EventCount from "..//components/Dashboard/EventCount";
import NextHoliday from "..//components/Dashboard/NextHoliday";
import { CalendarViewObject } from "../class/calendar.schema";

import { BarChart, BarChartDataSet } from "..//components/Dashboard/BarChart";
import {
  DoughnutChart,
  DoughnutChartData,
} from "../components/Dashboard/DoughnutChart";
import { PieChart } from "../components/Dashboard/PieChart";
import { useI18n } from "../i18n";

const Title = () => {
  const { t } = useI18n();
  const title = t("nav.dashboard") + " - " + t("meta.appName");
  return (
    <>
      <title>{title}</title>
      <h4 className="mb-4">{t("nav.dashboard")}</h4>
    </>
  );
};
const Content = () => {
  const { t } = useI18n();
  const [data, setData] = useState<DashboardData | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  const [previousEvents, setPreviousEvents] = useState<any[]>([]);
  const [nextHolidayData, setNextHolidayData] = useState<cal_holiday[]>();
  const [monthEventCount, setMonthEventCount] = useState(0);
  const [monthDays, setMonthDays] = useState(0);

  const [barChartTitle, setBarChartTitle] = useState<string>("");
  const [barChartLabels, setBarChartLabels] = useState<string[]>([]);
  const [barChartData, setBarChartData] = useState<BarChartDataSet[]>([]);

  const [douChartTile, setDouChartTitle] = useState<string>("");
  const [douChartLabel, setDouChartLabel] = useState<string[]>([]);
  const [douChartData, setDouChartData] = useState<DoughnutChartData>();

  const [pieChartTile, setPieChartTitle] = useState<string>("");
  const [pieChartLabel, setPieChartLabel] = useState<string[]>([]);
  const [pieChartData, setPieChartData] = useState<DoughnutChartData>();

  useEffect(() => {
    getDashboardData();
  }, []);

  const getDashboardData = async () => {
    const succFunc = (resp: AxiosResponse) => {
      const success = resp.status === 200 && resp.data != null;
      if (!success) {
        toast.error("Failed to fetch dashboard data.");
        return;
      }
      parseToDashboardData(resp.data);
      setData(resp.data);
    };
    const errFunc = (resp: AxiosResponse) => {
      toast.error("Failed to fetch dashboard data.");
    };
    await BackendApi.get(ApiRoute.dashboard, {}, succFunc, errFunc);
  };

  const parseToDashboardData = (data: DashboardData) => {
    if (!data) {
      return null;
    }

    parseEventData(data?.futureEvents ?? [], true, 3, setUpcomingEvents);
    parseEventData(data?.previousEvents ?? [], false, 3, setPreviousEvents);

    const nextHoliday =
      Array.isArray(data?.futureHolidays) && data?.futureHolidays.length > 0
        ? data?.futureHolidays.slice(0, 3)
        : undefined;
    setNextHolidayData(nextHoliday);

    const monthEventNum =
      Array.isArray(data?.monthEvents) && data?.monthEvents.length > 0
        ? data?.monthEvents.length
        : 0;
    setMonthEventCount(monthEventNum);
    setMonthDays(data?.daysInMonth ?? 0);
    barChartSetup(
      data?.eventSummaryByMonth,
      data?.calendarData as CalendarViewObject[],
    );
    doughnutChartSetup(
      data?.eventSummaryByMonth,
      data?.calendarData as CalendarViewObject[],
    );

    pieChartSetup(
      data?.calendarTotal,
      data?.calendarData as CalendarViewObject[],
    );
  };

  const barChartSetup = (
    summaryData?: EventSummaryByMonth,
    calendars?: CalendarViewObject[],
  ) => {
    if (
      !summaryData ||
      Object.keys(summaryData).length === 0 ||
      !calendars ||
      calendars.length === 0
    ) {
      return;
    }

    const yearName = moment(data?.now).format(`YYYY`);
    setBarChartTitle(`${t("dashboard.monthlySummary", { year: yearName })}`);

    const keyList = Object.keys(summaryData);
    const firstKey = keyList[0];
    const firstData = summaryData[firstKey];
    const labels = Object.keys(firstData);
    setBarChartLabels(labels);

    const dataSets: BarChartDataSet[] = [];
    for (const calendarId of keyList) {
      const find = calendars.find(
        (x) => x.id.toString() === calendarId.toString(),
      );
      if (!find) {
        continue;
      }
      const calendarColor = find.color;
      const calendarSummary = summaryData[calendarId];

      const dataNumList: number[] = [];
      for (const monthName of labels) {
        dataNumList.push(calendarSummary[monthName]);
      }
      dataSets.push({
        label: find.name,
        data: dataNumList,
        borderColor: calendarColor,
        backgroundColor: calendarColor,
      });
    }
    setBarChartData(dataSets);
  };

  const doughnutChartSetup = (
    summaryData?: EventSummaryByMonth,
    calendars?: CalendarViewObject[],
  ) => {
    if (
      !summaryData ||
      Object.keys(summaryData).length === 0 ||
      !calendars ||
      calendars.length === 0
    ) {
      return;
    }
    const yearName = moment(data?.now).format(`YYYY`);
    setDouChartTitle(`${t("dashboard.eventDistribution", { year: yearName })}`);

    const keyList = Object.keys(summaryData);
    const firstKey = keyList[0];
    const firstData = summaryData[firstKey];
    const monthList = Object.keys(firstData);

    const labels: string[] = [];
    const valueList: number[] = [];
    const colorList: string[] = [];
    for (const key of keyList) {
      const find = calendars.find((x) => x.id.toString() === key.toString());
      if (!find) {
        continue;
      }
      const calendarName = find.name;
      const calendarColor = find.color;
      const calendarSummary = summaryData[key];
      let sum = 0;
      for (const month of monthList) {
        sum += calendarSummary[month];
      }
      valueList.push(sum);
      colorList.push(calendarColor);
      labels.push(calendarName);
    }
    setDouChartLabel(labels);
    setDouChartData({
      data: valueList,
      borderColor: colorList,
      backgroundColor: colorList,
    });
  };

  const pieChartSetup = (
    calendarTotal?: CalendarTotal,
    calendars?: CalendarViewObject[],
  ) => {
    if (
      !calendarTotal ||
      Object.keys(calendarTotal).length === 0 ||
      !calendars ||
      calendars.length === 0
    ) {
      return;
    }
    setPieChartTitle(`${t("dashboard.totalEventDistribution")}`);
    const keyList = Object.keys(calendarTotal);

    const labels: string[] = [];
    const valueList: number[] = [];
    const colorList: string[] = [];
    for (const key of keyList) {
      const find = calendars.find((x) => x.id.toString() === key.toString());
      if (!find) {
        continue;
      }
      const calendarName = find.name;
      const calendarColor = find.color;
      const count = calendarTotal[key];

      valueList.push(count);
      colorList.push(calendarColor);
      labels.push(calendarName);
    }
    setPieChartLabel(labels);
    setPieChartData({
      data: valueList,
      borderColor: colorList,
      backgroundColor: colorList,
    });
  };

  const parseEventData = (
    events: cal_record[],
    isFuture: boolean,
    takeNum: number,
    setValueFunc: React.Dispatch<React.SetStateAction<any[]>>,
  ) => {
    if (Array.isArray(events)) {
      const take = events.length >= takeNum ? takeNum : events.length;
      const filtered = events
        .filter(
          (x) =>
            moment(x.start_date).isValid() &&
            (isFuture
              ? moment(x.start_date).isAfter(moment())
              : moment(x.start_date).isBefore(moment())),
        )
        .sort((a, b) => {
          if (isFuture) {
            return moment(a.start_date).diff(moment(b.start_date));
          }
          return moment(b.start_date).diff(moment(a.start_date));
        })
        .slice(0, take);

      if (filtered.length > 0 && takeNum > filtered.length) {
        for (let i = 0; i <= takeNum - filtered.length; i++) {
          filtered.push({
            id: 0,
            name: "",
          } as cal_record);
        }
      }
      setValueFunc(filtered);
    }
  };

  return (
    <>
      <Stack direction="horizontal" gap={3}>
        <NextHoliday holidays={nextHolidayData} />
        <EventCount noOfEvents={monthEventCount} totalDays={monthDays} />
        <EventCard
          title={t("dashboard.nextEvents")}
          events={upcomingEvents}
          calendarData={data?.calendarData ?? []}
        />
        <EventCard
          title={t("dashboard.prevEvents")}
          events={previousEvents}
          calendarData={data?.calendarData ?? []}
        />
      </Stack>
      <Stack direction="horizontal" gap={3} style={{ marginTop: 12 }}>
        <BarChart
          width="40rem"
          chartTitle={barChartTitle}
          labels={barChartLabels}
          datasets={barChartData}
        />
        <DoughnutChart
          width="21rem"
          chartTitle={douChartTile}
          labels={douChartLabel}
          chartData={douChartData as DoughnutChartData}
        />
        <PieChart
          width="21rem"
          chartTitle={pieChartTile}
          labels={pieChartLabel}
          chartData={pieChartData as DoughnutChartData}
        />
      </Stack>
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          className: "",
          style: common.toasterStyle,
        }}
      />
    </>
  );
};
const Record = () => {
  return <ContentLayout header={<Title />} content={<Content />} />;
};
export default Record;
